import React from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';
import ProductCard from '../components/ProductCard';
import AllyProductsCarousel from '../components/AllyProductsCarousel';
import OfflineBanner from '../components/OfflineBanner';
import MailingSignup from '../components/MailingSignup';
import { fetchProducts } from '../services/productsService';
import { CACHE_KEYS } from '../services/kioskCache';
import useKioskData from '../hooks/useKioskData';
import { useKiosk } from '../contexts/KioskContext';
import { getHomeTitles } from '../config/homeTitles';
import loadingAnimation from '../assets/loading.json';

const HomePage = () => {
  const { isKiosk } = useKiosk();
  const { t } = useTranslation('home');

  const { data, loading, error, fromCache } = useKioskData(CACHE_KEYS.products, fetchProducts);

  // Kiosk titles are event-specific Spanish copy; storefront titles come
  // from the i18n catalog so they swap with the language toggle.
  const COLLAB_EMAIL = 'hey@bluppimart.com';
  const titles = isKiosk
    ? getHomeTitles(true)
    : {
        hero: t('hero'),
        preorders: t('preorders'),
        collaboration: {
          leadLine1: t('collaboration.leadLine1'),
          leadLine2: t('collaboration.leadLine2'),
          email: COLLAB_EMAIL,
          trailing: t('collaboration.trailing'),
        },
      };

  const displayProducts = data ?? [];

  // Sort comparator: by displayOrder ascending (nulls last), then isSoldOut last as tiebreaker.
  const byDisplayOrder = (a, b) => {
    const ao = a.displayOrder;
    const bo = b.displayOrder;
    if (ao == null && bo == null) {
      return (a.isSoldOut ? 1 : 0) - (b.isSoldOut ? 1 : 0);
    }
    if (ao == null) return 1;
    if (bo == null) return -1;
    return ao - bo;
  };

  // Preorder products (isPreorder === true) for "Preventas" section
  const preorderProducts = displayProducts.filter(p => p.isPreorder && !p.isSoldOut).sort(byDisplayOrder);

  // All non-preorder products as a single flat list sorted by displayOrder
  const otherProducts = displayProducts.filter(p => !p.isPreorder && !p.isSoldOut).sort(byDisplayOrder);

  if (loading) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <Lottie
          animationData={loadingAnimation}
          loop
          style={{ width: 200, height: 200, margin: '0 auto' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
        <p style={{ fontSize: '2rem', fontWeight: '600', color: '#333', marginBottom: '1rem' }}>
          {t('errorFallback')}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Home Section */}
      <section className="home-section" style={{
        padding: '2rem 0',
        textAlign: 'center',
        backgroundColor: '#fffbf5'
      }}>
        <div className="container">
          <h5 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#000',
            marginBottom: 'none',
            lineHeight: '1.0',
            fontFamily: "'Gambado Sans', sans-serif"
          }}>
            {titles.hero}
          </h5>
        </div>
      </section>

      {/* Products — single flat grid sorted by displayOrder */}
      {otherProducts.length > 0 && (
        <section className="products-section">
          <div className="container">
            <div className="products-grid">
              {otherProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      <OfflineBanner show={fromCache} />

      {/* Empty State */}
      {displayProducts.length === 0 && !loading && !error && (
        <section className="products-section">
          <div className="container" style={{ textAlign: 'center', padding: '4rem 0' }}>
            <p style={{ fontSize: '1.2rem', color: '#666' }}>{t('empty')}</p>
          </div>
        </section>
      )}

      {/* Featured ally products — pilot for El Tesoro del Juguete. Skipped in
          kiosk mode: it's a live fetch outside useKioskData's offline cache,
          and kiosk nav is meant to stay simplified. */}
      {!isKiosk && <AllyProductsCarousel />}

      {/* Preventas: preorder products (at the bottom) */}
      {preorderProducts.length > 0 && (
        <section className="products-section">
          <div className="container">
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#333',
              marginBottom: '1.5rem',
              fontFamily: "'Gambado Sans', sans-serif",
              textAlign: 'center'
            }}>
              {titles.preorders}
            </h2>
            <div className="products-grid">
              {preorderProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Collaboration Section */}
      {titles.collaboration?.leadLine1 && (
      <section className="home-section" style={{
        padding: '2rem 0',
        textAlign: 'center',
        backgroundColor: '#fffbf5'
      }}>
        <div className="container">
          <h5 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#000',
            marginBottom: 'none',
            lineHeight: '1.0',
            fontFamily: "'Gambado Sans', sans-serif"
          }}>
            {titles.collaboration.leadLine1} <br />
            {titles.collaboration.leadLine2}{' '}
            <a
              href={`mailto:${titles.collaboration.email}`}
              style={{
                color: '#2ca9e1',
                textDecoration: 'none',
                transition: 'opacity 0.2s ease',
                fontFamily: "'Gambado Sans', sans-serif"
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              {titles.collaboration.email}
            </a>
            {titles.collaboration.trailing}
          </h5>
        </div>
      </section>
      )}

      {/* Raffle / event / newsletter sign-up — not on shared kiosk screens */}
      {!isKiosk && <MailingSignup />}
    </div>
  );
};

export default HomePage;
